import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { X, Mail, Users } from 'lucide-react';
import { useDanceStore } from '../store/useDanceStore';
import { useStorageStore } from '../store/useStorageStore';
import { fetchWithAuth } from '../lib/api';
import { trackShareEvent } from '../lib/analytics';
import { Contact } from '../types';

export const ShareDialog: React.FC = () => {
  const { t } = useTranslation();
  const shareDialog = useDanceStore(state => state.shareDialog);
  const closeShareDialog = useDanceStore(state => state.closeShareDialog);
  const saveSequence = useStorageStore(state => state.saveSequence);
  const setToastMessage = useDanceStore(state => state.setToastMessage);

  const [emailInput, setEmailInput] = useState('');
  const [collaborators, setCollaborators] = useState<{ email: string; exists: boolean | null }[]>([]);
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [checkingEmail, setCheckingEmail] = useState(false);
  const [saving, setSaving] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  useEffect(() => {
    if (shareDialog?.isOpen && shareDialog.sequence) {
      trackShareEvent('dialog_opened', { sequence_id: shareDialog.sequence.id });
      setErrorMsg(null);
      setEmailInput('');

      // Fetch user's saved contacts
      fetchWithAuth('/api/contacts')
        .then((data) => {
          if (Array.isArray(data)) {
            setContacts(data);
          }
        })
        .catch(() => setContacts([]));
      
      const initialEmails = shareDialog.sequence.collaborators || [];
      
      // Initialize collaborator pills. Since we don't know exists status initially,
      // we check all of them in parallel so they display the correct green/red styling.
      const loadExistingCollaborators = async () => {
        setCheckingEmail(true);
        const pills = await Promise.all(
          initialEmails.map(async (email) => {
            try {
              const res = await fetchWithAuth(`/api/users/check?email=${encodeURIComponent(email)}`);
              return { email, exists: !!res?.exists };
            } catch {
              return { email, exists: true }; // Fallback to green/safe if query fails
            }
          })
        );
        setCollaborators(pills);
        setCheckingEmail(false);
      };
      
      if (initialEmails.length > 0) {
        loadExistingCollaborators();
      } else {
        setCollaborators([]);
      }
    }
  }, [shareDialog]);

  if (!shareDialog || !shareDialog.isOpen) return null;

  const sequence = shareDialog.sequence;

  const isOriginallyShared = !!(sequence.collaborators && sequence.collaborators.length > 0);
  const isCurrentlyShared = collaborators.length > 0;
  const hasChanges = (() => {
    const original = [...(sequence.collaborators || [])].sort();
    const current = collaborators.map((c) => c.email).sort();
    return original.length !== current.length || !original.every((v, i) => v === current[i]);
  })();

  const btnText = (() => {
    if (saving) return t('sharing.sharing_btn', 'Sharing...');
    if (isOriginallyShared && !isCurrentlyShared) return t('sharing.menu_unshare', 'Unshare');
    if (isOriginallyShared && isCurrentlyShared) return t('common.update', 'Update');
    return t('sharing.share_btn', 'Share');
  })();

  const btnClass = isOriginallyShared && !isCurrentlyShared ? 'share-btn-save danger' : 'share-btn-save primary';

  const validateEmail = (email: string) => {
    const re = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    return re.test(email);
  };

  const handleAddEmail = async (email: string) => {
    setErrorMsg(null);
    const trimmed = email.trim().toLowerCase();
    if (!trimmed) return;

    if (!validateEmail(trimmed)) {
      setErrorMsg(t('sharing.invalid_email', 'Please enter a valid email address.'));
      return;
    }

    if (collaborators.some((c) => c.email === trimmed)) {
      setErrorMsg(t('sharing.already_added', 'This email is already added.'));
      return;
    }

    setCheckingEmail(true);
    try {
      const res = await fetchWithAuth(`/api/users/check?email=${encodeURIComponent(trimmed)}`);
      const exists = !!res?.exists;
      
      setCollaborators((prev) => [...prev, { email: trimmed, exists }]);
      setEmailInput('');
      trackShareEvent('email_added', { user_exists: exists });
    } catch (err: any) {
      setErrorMsg(err.message || t('sharing.check_failed', 'Failed to check user account.'));
    } finally {
      setCheckingEmail(false);
    }
  };

  const handleRemoveEmail = (email: string) => {
    setCollaborators((prev) => prev.filter((c) => c.email !== email));
    trackShareEvent('email_removed', { email });
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' || e.key === ' ' || e.key === ',') {
      e.preventDefault();
      handleAddEmail(emailInput);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setErrorMsg(null);
    try {
      const emails = collaborators.map((c) => c.email);
      const originalEmails = sequence.collaborators || [];
      const newlyAddedCount = emails.filter((e) => !originalEmails.includes(e)).length;

      // Auto-upsert collaborators into user's contacts list
      await Promise.all(
        emails.map((email) =>
          fetchWithAuth('/api/contacts', {
            method: 'POST',
            body: JSON.stringify({ contactEmail: email }),
          }).catch((err) => console.warn(`[ShareDialog] Failed to upsert contact ${email}:`, err))
        )
      );

      // Save changes
      saveSequence({
        ...sequence,
        collaborators: emails,
      });

      trackShareEvent('permissions_saved', {
        total_collaborators: emails.length,
        newly_added: newlyAddedCount,
      });

      setToastMessage(t('sharing.success_save', 'Sharing permissions updated successfully!'), 'info');
      closeShareDialog();
    } catch (err: any) {
      setErrorMsg(err.message || t('sharing.failed_save', 'Failed to save sharing settings.'));
    } finally {
      setSaving(false);
    }
  };

  const availableContacts = contacts.filter(
    (c) => !collaborators.some((col) => col.email === c.contactEmail.toLowerCase())
  );

  return (
    <div className="share-dialog-overlay">
      <div className="glass-panel share-dialog-content">
        <div className="share-dialog-header">
          <div className="share-dialog-title-container">
            <h3 className="share-dialog-title">{t('sharing.title', 'Share "{{name}}"', { name: sequence.name })}</h3>
          </div>
          <button className="share-dialog-close-btn" onClick={closeShareDialog}>
            <X size={18} />
          </button>
        </div>

        <div className="share-dialog-layout">
          {/* Main Interface (Left Column) */}
          <div className="share-dialog-main">
            <div className="share-dialog-body">
              <div className="share-dialog-input-wrapper">
                <Mail size={16} className="share-input-icon" />
                <input
                  type="text"
                  className="share-dialog-input"
                  placeholder={t('sharing.placeholder', 'Add collaborator by email...')}
                  value={emailInput}
                  onChange={(e) => setEmailInput(e.target.value)}
                  onKeyDown={handleKeyDown}
                  onBlur={() => handleAddEmail(emailInput)}
                  disabled={checkingEmail || saving}
                />
                {checkingEmail && <div className="share-dialog-spinner" />}
              </div>

              {errorMsg && <div className="share-error-message">{errorMsg}</div>}

              <div className="share-info-text">
                {t('sharing.instruction', 'Press Enter, Space, or Comma to add.')}
              </div>

              <div className="share-pills-container">
                <h4 className="share-section-title">{t('sharing.collaborators_list', 'Collaborators')} ({collaborators.length})</h4>
                {collaborators.length === 0 ? (
                  <div className="share-empty-list">{t('sharing.no_collaborators', 'No one has been shared with yet.')}</div>
                ) : (
                  <div className="share-pills">
                    {collaborators.map((c) => (
                      <div
                        key={c.email}
                        className={`share-pill ${c.exists === true ? 'green' : c.exists === false ? 'red' : ''}`}
                        title={c.exists === true 
                          ? t('sharing.registered_user', 'Registered User') 
                          : t('sharing.unregistered_user', 'Unregistered User (Will receive email invite)')
                        }
                      >
                        <span>{c.email}</span>
                        <button
                          className="share-pill-remove"
                          onClick={() => handleRemoveEmail(c.email)}
                          disabled={saving}
                        >
                          <X size={10} />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <div className="share-dialog-footer">
              <button className="share-btn-cancel" onClick={closeShareDialog} disabled={saving}>
                {t('common.cancel')}
              </button>
              <button className={btnClass} onClick={handleSave} disabled={saving || checkingEmail || !hasChanges}>
                {btnText}
              </button>
            </div>
          </div>

          {/* Contacts Side Panel (Right Column) */}
          <div className="share-dialog-side-panel">
            <div className="share-contacts-header">
              <Users size={14} className="share-contacts-icon" />
              <h4 className="share-contacts-title">{t('sharing.contacts', 'Contacts')}</h4>
            </div>
            <div className="share-contacts-list">
              {contacts.length === 0 ? (
                <div className="share-empty-list">{t('sharing.no_contacts', 'No contacts saved.')}</div>
              ) : (
                contacts.map((c) => {
                  const isAdded = collaborators.some((col) => col.email === c.contactEmail.toLowerCase());
                  return (
                    <button
                      key={c.id}
                      type="button"
                      className={`share-contact-item ${c.isRegistered ? 'green' : 'red'} ${isAdded ? 'added' : 'clickable'}`}
                      onClick={() => !isAdded && handleAddEmail(c.contactEmail)}
                      disabled={isAdded || checkingEmail || saving}
                      title={
                        isAdded
                          ? t('sharing.already_added', 'Already added')
                          : c.isRegistered
                          ? t('sharing.registered_user', 'Registered User')
                          : t('sharing.unregistered_user', 'Unregistered User (Will receive email invite)')
                      }
                    >
                      <div className="share-contact-info">
                        <span className="share-contact-name">
                          {c.contactNickname || c.contactEmail}
                        </span>
                        {c.contactNickname && (
                          <span className="share-contact-email">{c.contactEmail}</span>
                        )}
                      </div>
                    </button>
                  );
                })
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};


